// src/theme/ThemeProvider.jsx

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useUser } from "../contexts/UserContext";
import {
  fetchGymTheme,
  fetchThemeVersion,
  clearThemeCache,
  DEFAULT_THEME,
} from "../services/gymTheme.service";
import { ThemeContext } from "./ThemeContext";

const VERSION_CHECK_INTERVAL_MS = 60 * 1000;

// ─────────────────────────────────────────────────────────────────────────────
// Todas as props gerenciadas via inline style.
// Ao voltar para tema default, TODAS são removidas para que
// global.css + [data-theme="dark"] retomem o controle.
// ─────────────────────────────────────────────────────────────────────────────
const MANAGED_PROPS = [
  // Light
  "--gym-primary",
  "--gym-primary-dark",
  "--gym-accent",
  "--color-accent",       // fix leak: era setada inline mas não removida ao voltar p/ default
  "--navbar-inactive",    // cor do texto/ícone INATIVO da navbar
  "--color-brand-primary",// nova "cor principal" da marca (default #201e1e)
  "--loading-bg",
  "--navbar-bg",
  "--navbar-active",
  "--tab-active-bg",
  "--tab-active-text",
  "--btn-primary-bg",
  "--btn-primary-text",
  "--label-highlight",
  "--card-accent-bg",
  "--lesvita-bg",
  // Dark gym — lidas por [data-theme="dark"][data-brand="gym"] em themes.css
  "--gym-primary-dark",
  "--gym-accent-dark",
  "--gym-navbar-bg-dark",
  "--gym-navbar-active-dark",
  "--gym-splash-dark",
  "--gym-surface-dark",
  "--gym-navbar-inactive-dark",
  "--gym-brand-primary-dark",
  "--gym-app-bg-dark",
];

// Modo atual: mesma fonte do ThemeContext (que grava data-theme no <html>).
// Ler o atributo evita acoplar este provider ao contexto de claro/escuro e
// funciona também durante o anti-flash (o script do index.html já setou data-theme).
const getMode = () =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

// ─────────────────────────────────────────────────────────────────────────────
// applyThemeToDom (MODE-AWARE)
//
// Default (is_default: true):
//   Remove todas as props inline → global.css + themes.css [data-theme] assumem.
//
// Academia (is_default: false):
//   Para cada var-base faz UM setProperty com o valor do MODO ATUAL.
//   Inline vence stylesheet, então a var-base carrega sempre o valor do modo
//   correto (corrige o antigo "valor claro vencendo no dark").
//   Fallback por campo: valor do modo → valor do outro modo → removeProperty
//   (aí themes.css assume). Os intermediários --gym-*-dark seguem sendo setados
//   como compat/fallback p/ vars que NÃO setamos inline aqui (ex.: --color-primary,
//   --color-bg-surface lidas por themes.css [data-theme="dark"][data-brand="gym"]).
// ─────────────────────────────────────────────────────────────────────────────
const applyThemeToDom = (theme) => {
  const root = document.documentElement;

  if (theme.is_default) {
    MANAGED_PROPS.forEach((prop) => root.style.removeProperty(prop));
    root.setAttribute("data-gym", "lesvita");
    return;
  }

  const isDark = getMode() === "dark";

  // Escolhe o valor do modo atual; se vazio, cai no outro modo; se ambos vazios,
  // removeProperty → deixa themes.css/global.css assumir a var.
  const setModeVar = (cssVar, lightVal, darkVal) => {
    const chosen = isDark ? (darkVal || lightVal) : (lightVal || darkVal);
    if (chosen) root.style.setProperty(cssVar, chosen);
    else        root.style.removeProperty(cssVar);
  };

  // ── Vars-base mode-aware ─────────────────────────────────────────────────
  setModeVar("--gym-primary",     theme.primary_color,        theme.dark_primary_color);
  setModeVar("--gym-accent",      theme.secondary_color,      theme.dark_accent_color);
  setModeVar("--color-accent",    theme.secondary_color,      theme.dark_accent_color);
  setModeVar("--loading-bg",      theme.splash_bg_color || theme.background_color, theme.dark_splash_bg_color);
  setModeVar("--navbar-bg",       theme.navbar_bg_color,      theme.dark_navbar_bg_color);
  setModeVar("--navbar-active",   theme.navbar_active_color,  theme.dark_navbar_active_color);
  setModeVar("--navbar-inactive", theme.navbar_inactive_color, theme.dark_navbar_inactive_color);

  // Sem par dark hoje → valor único (claro) nos dois modos.
  setModeVar("--tab-active-bg",   theme.tab_active_bg,        null);
  setModeVar("--tab-active-text", theme.tab_active_text,      null);
  setModeVar("--btn-primary-bg",  theme.button_primary_bg,    null);
  setModeVar("--btn-primary-text",theme.button_primary_text,  null);
  setModeVar("--label-highlight", theme.label_highlight_color, null);
  setModeVar("--card-accent-bg",  theme.card_accent_bg,       null);

  // Cor principal da marca — sempre resolve (default #201e1e), mode-aware.
  const brandLight = theme.brand_main_color || "#201e1e";
  const brandDark  = theme.dark_brand_main_color || brandLight;
  root.style.setProperty("--color-brand-primary", isDark ? brandDark : brandLight);

  // Fundo total do app (mode-aware):
  //   dark  → dark_app_bg_color → app_bg_color → removeProperty (themes.css
  //           gym-dark reconstrói o gradiente via --gym-splash-dark/surface-dark).
  //   light → app_bg_color → gradiente atual (background_color, primary_color),
  //           preservando a aparência das academias não repopuladas.
  if (isDark) {
    const dbg = theme.dark_app_bg_color || theme.app_bg_color;
    if (dbg) root.style.setProperty("--lesvita-bg", dbg);
    else     root.style.removeProperty("--lesvita-bg");
  } else {
    root.style.setProperty(
      "--lesvita-bg",
      theme.app_bg_color || `linear-gradient(180deg, ${theme.background_color}, ${theme.primary_color})`
    );
  }

  // ── Intermediários dark (compat/fallback p/ themes.css) ───────────────────
  // Mantidos p/ as vars que NÃO setamos inline: --color-primary, --color-bg-surface
  // e o gradiente dark de --lesvita-bg. Não são mais o mecanismo primário das
  // vars-base acima (essas já carregam o valor do modo).
  const applyDark = (cssVar, value) => {
    if (value) root.style.setProperty(cssVar, value);
    else       root.style.removeProperty(cssVar);
  };

  applyDark("--gym-primary-dark",  theme.dark_primary_color);
  applyDark("--gym-accent-dark",   theme.dark_accent_color);
  applyDark("--gym-splash-dark",   theme.dark_splash_bg_color);
  applyDark("--gym-surface-dark",  theme.dark_surface_color);

  root.setAttribute("data-gym", theme.gym_id || "lesvita");
};

// Anti-flash: aplica cache síncrono antes do React montar.
// Só aplica se for tema de academia (não default) — evita freeze no LesVita puro.
const applyThemeCacheSync = () => {
  try {
    const raw = localStorage.getItem("lesvita_gym_theme");
    if (!raw) return;
    const { theme } = JSON.parse(raw);
    if (theme?.primary_color && theme.is_default === false) {
      applyThemeToDom(theme);
    }
  } catch {}
};

applyThemeCacheSync();

// ─────────────────────────────────────────────────────────────────────────────

const ThemeProvider = ({ children }) => {
  const { user } = useUser();
  const [theme, setTheme]               = useState(DEFAULT_THEME);
  const [isThemeReady, setIsThemeReady] = useState(false);
  const prevGymCodeRef  = useRef(null);
  const currentThemeRef = useRef(DEFAULT_THEME);
  const intervalRef     = useRef(null);

  const loadTheme = useCallback(async (gymReferral) => {
    const resolved = await fetchGymTheme(gymReferral);
    currentThemeRef.current = resolved;
    setTheme(resolved);
    applyThemeToDom(resolved);
    setIsThemeReady(true);
  }, []);

  const startVersionPolling = useCallback((gymCode, gymReferral) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(async () => {
      const remoteVersion = await fetchThemeVersion(gymCode);
      const cachedVersion = currentThemeRef.current?.theme_version;
      if (remoteVersion && remoteVersion !== cachedVersion) {
        console.info("[ThemeProvider] Tema atualizado no painel, rebuscando...");
        clearThemeCache();
        await loadTheme(gymReferral);
      }
    }, VERSION_CHECK_INTERVAL_MS);
  }, [loadTheme]);

  useEffect(() => {
    const gymReferral = user?.GYM_REFERRAL ?? null;
    const gymCode     = gymReferral?.CODE ?? null;

    if (gymCode !== prevGymCodeRef.current) {
      if (prevGymCodeRef.current) clearThemeCache();
      prevGymCodeRef.current = gymCode;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    let cancelled = false;
    const init = async () => {
      await loadTheme(gymReferral);
      if (cancelled) return;
      if (gymCode) startVersionPolling(gymCode, gymReferral);
    };
    init();
    return () => { cancelled = true; };
  }, [user?.GYM_REFERRAL?.CODE, loadTheme, startVersionPolling]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Mode-aware: re-aplica as vars ao trocar claro/escuro SEM reload.
  // O ThemeContext (toggle) grava data-theme no <html>; observamos essa troca e
  // reescrevemos as vars-base com o valor do modo novo (usa o tema atual do ref).
  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.attributeName === "data-theme")) {
        applyThemeToDom(currentThemeRef.current);
      }
    });
    observer.observe(target, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme,
      isDefaultTheme: theme.is_default ?? true,
      isThemeReady,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;